import { create } from 'zustand';
import { getMeTimeClock } from '@/api/generated/me-time-clock/me-time-clock';
import { getTimeClockAdmin } from '@/api/generated/time-clock-admin/time-clock-admin';
import { customInstance } from '@/api/mutator/custom-instance';
import { parseAxiosError, translateApiError } from '@/errors';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';
import { addIsoDays, isoDate, parseUtcTimestamp } from '@/utils/dateUtils';
import type {
  FacilityFlowApiControllersPlanningMeTimeClockControllerConfirmDayRequestDto,
  FacilityFlowApiControllersPlanningMeTimeClockControllerRecordEventRequestDto,
  FacilityFlowCoreModulesFleetDtosEndVehicleTripRequest,
  FacilityFlowCoreModulesFleetDtosStartVehicleTripRequest,
  FacilityFlowCoreModulesPlanningEntitiesDriverRole,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType,
  FacilityFlowCoreModulesPlanningEntitiesWorkLocation,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockFilter,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput,
} from '@/api/generated/models';
import {
  FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as TimeClockDayStatusEnum,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as TimeClockEventTypeEnum,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockFilter as TimeClockFilterEnum,
} from '@/api/generated/models';

const api = getMeTimeClock();
const adminApi = getTimeClockAdmin();

export type TimeClockFilterKey = 'open' | 'thisWeek' | 'thisMonth' | 'lastMonth';

const filterMap: Record<TimeClockFilterKey, FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockFilter> = {
  open: TimeClockFilterEnum.NUMBER_0,
  thisWeek: TimeClockFilterEnum.NUMBER_1,
  thisMonth: TimeClockFilterEnum.NUMBER_2,
  lastMonth: TimeClockFilterEnum.NUMBER_3,
};

const eventTypeMap = {
  clockIn: TimeClockEventTypeEnum.NUMBER_1,
  clockOut: TimeClockEventTypeEnum.NUMBER_2,
  breakStart: TimeClockEventTypeEnum.NUMBER_3,
  breakEnd: TimeClockEventTypeEnum.NUMBER_4,
} as const;

export type TimeClockEventKey = keyof typeof eventTypeMap;

/** 1 = Fahrer, 2 = Beifahrer, 3 = kein Fahrer (FACF-117). Travels as an int. */
export type DriverRole = FacilityFlowCoreModulesPlanningEntitiesDriverRole;

/** A trip started with the clock-in or ended with the clock-out (FAFD-68, ADR-0027). */
export interface StampTrip {
  trip?: FacilityFlowCoreModulesFleetDtosStartVehicleTripRequest;
  endTrip?: FacilityFlowCoreModulesFleetDtosEndVehicleTripRequest;
}

/**
 * 1 = Büro, 2 = Homeoffice. Chosen at clock-in by employees allowed to work from home and stored
 * on the clock-in only. Travels as an int.
 */
export type WorkLocation = FacilityFlowCoreModulesPlanningEntitiesWorkLocation;

type TimeClockDay = FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto;
type TimeClockEvent = FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto;

/** Where the employee stands on a day, derived from that day's last event. */
export type ClockState = 'idle' | 'in' | 'break' | 'out';

/** Events in chronological order — the API does not guarantee any order. */
export function sortEventsByTime(events: TimeClockEvent[] | null | undefined): TimeClockEvent[] {
  if (!events) return [];
  return [...events].sort(
    (a, b) =>
      (parseUtcTimestamp(a.timestamp)?.getTime() ?? 0) -
      (parseUtcTimestamp(b.timestamp)?.getTime() ?? 0),
  );
}

/**
 * The one place that turns a day's events into a clock state. Every UI that shows or acts on
 * the state (header widget, dashboard, time clock page, checklists) goes through here, so they
 * cannot disagree about whether someone is clocked in.
 */
export function getClockState(entry: TimeClockDay | null | undefined): ClockState {
  const events = sortEventsByTime(entry?.events);
  const last = events[events.length - 1]?.eventType;
  switch (last) {
    case TimeClockEventTypeEnum.NUMBER_1:
    case TimeClockEventTypeEnum.NUMBER_4:
      return 'in';
    case TimeClockEventTypeEnum.NUMBER_3:
      return 'break';
    case TimeClockEventTypeEnum.NUMBER_2:
      return 'out';
    default:
      return 'idle';
  }
}

/** Clock state of `entry` if it belongs to `dayIso`, otherwise 'idle' (stale after midnight). */
export function getClockStateOn(
  entry: TimeClockDay | null | undefined,
  dayIso: string,
): ClockState {
  return entry?.date === dayIso ? getClockState(entry) : 'idle';
}

function isClockedIn(entry: TimeClockDay): boolean {
  const state = getClockState(entry);
  return state === 'in' || state === 'break';
}

/**
 * Yesterday's day while it is still clocked in — a night shift running past midnight (FACF-149).
 * The server caps a forgotten clock-out at `MaxWorkPerDay` whenever it hands out open days, so
 * a day that still reads clocked in here has not run longer than that: it is a running shift.
 */
export function getRunningShift(openDays: TimeClockDay[], todayIso: string): TimeClockDay | null {
  const yesterdayIso = addIsoDays(todayIso, -1);
  return openDays.find((day) => day.date === yesterdayIso && isClockedIn(day)) ?? null;
}

/**
 * The day the clock buttons act on: a running night shift until today has its own stamps, then
 * today. `date` is what break and clock-out send — the backend files them under the shift's day.
 */
export function getActiveDay(
  todayEntry: TimeClockDay | null | undefined,
  openDays: TimeClockDay[],
  todayIso: string,
): { date: string; entry: TimeClockDay | null; isRunningShift: boolean } {
  const todayHasStamps = todayEntry?.date === todayIso && (todayEntry.events?.length ?? 0) > 0;
  const runningShift = todayHasStamps ? null : getRunningShift(openDays, todayIso);
  if (runningShift?.date) {
    return { date: runningShift.date, entry: runningShift, isRunningShift: true };
  }
  return {
    date: todayIso,
    entry: todayEntry?.date === todayIso ? todayEntry : null,
    isRunningShift: false,
  };
}

/**
 * A past day that was clocked into but never clocked out of. Yesterday's running night shift is
 * not missing anything — it is ended with the normal clock-out.
 */
export function isMissingClockOut(entry: TimeClockDay, todayIso: string): boolean {
  if (!entry.date || entry.date >= todayIso) return false;
  if (entry.date === addIsoDays(todayIso, -1)) return false;
  return isClockedIn(entry);
}

/** Whether any past day still lacks its clock-out — the backend then refuses a new clock-in. */
export function hasMissingClockOut(days: TimeClockDay[], todayIso: string): boolean {
  return days.some((day) => isMissingClockOut(day, todayIso));
}

/** A past day with a complete clock-out that the employee has not confirmed yet. */
export function isUnconfirmedComplete(entry: TimeClockDay, todayIso: string): boolean {
  if (!entry.date || entry.date >= todayIso) return false;
  return entry.status === TimeClockDayStatusEnum.NUMBER_1 && getClockState(entry) === 'out';
}

/** Localized backend error (e.g. planning.timeClock.previousDayOpen) instead of axios' text. */
function toMessage(err: unknown): string {
  return translateApiError(parseAxiosError(err));
}

interface TimeclockState {
  todayEntry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto | null;
  historyEntries: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto[];
  /** Every unconfirmed day of the employee, any date — kept apart from the page's history. */
  openDays: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto[];
  currentFilter: TimeClockFilterKey;
  loading: boolean;
  error: string | null;
  successMessage: string | null;

  fetchToday: (date?: string) => Promise<void>;
  recordEvent: (
    eventType: TimeClockEventKey,
    date?: string,
    timestamp?: string,
    trip?: StampTrip,
    workLocation?: WorkLocation,
  ) => Promise<void>;
  confirmDay: (date: string) => Promise<void>;
  fetchHistory: (filter: TimeClockFilterKey) => Promise<void>;
  fetchOpenDays: () => Promise<void>;
  correctDay: (
    employeeId: string,
    date: string,
    events: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput[],
    correctionNote: string,
  ) => Promise<void>;
  selfCorrectDay: (
    date: string,
    events: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput[],
  ) => Promise<void>;
  clearError: () => void;
  clearSuccess: () => void;
}

function todayDateString(): string {
  return isoDate(new Date());
}

export const useTimeclockStore = create<TimeclockState>((set, get) => ({
  todayEntry: null,
  historyEntries: [],
  openDays: [],
  currentFilter: 'open',
  loading: false,
  error: null,
  successMessage: null,

  fetchToday: async (date?: string) => {
    set({ loading: true, error: null });
    try {
      const result = await api.getApiV1MeTimeclock(date ? { date } : undefined);
      const entry = result ?? null;
      set({ todayEntry: entry, loading: false });
    } catch (err) {
      set({ error: toMessage(err), loading: false, todayEntry: null });
    }
  },

  recordEvent: async (
    eventType: TimeClockEventKey,
    date?: string,
    timestamp?: string,
    trip?: StampTrip,
    workLocation?: WorkLocation,
  ) => {
    const targetDate = date || todayDateString();
    set({ loading: true, error: null, successMessage: null });

    const payload: FacilityFlowApiControllersPlanningMeTimeClockControllerRecordEventRequestDto = {
      date: targetDate,
      eventType: eventTypeMap[eventType] as FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType,
      timestamp: timestamp ?? null,
      ...(trip?.trip ? { trip: trip.trip } : {}),
      ...(trip?.endTrip ? { endTrip: trip.endTrip } : {}),
      ...(workLocation ? { workLocation } : {}),
    };

    try {
      const result = await api.postApiV1MeTimeclockEvent(payload);
      // why: corrections and late clock-outs record events for past days — those results
      // must not replace today's entry, or every view would show that day's state as today's.
      // A night shift's break or clock-out lands on yesterday's open day: swap it in right away,
      // so the buttons do not show the old state until the refetch below returns.
      set((s) => ({
        ...(targetDate === todayDateString() ? { todayEntry: result } : {}),
        openDays: s.openDays.map((day) => (day.date === targetDate ? result : day)),
        loading: false,
        successMessage: i18n.t(`timeclock:messages.${eventType}`),
      }));

      get().fetchHistory(get().currentFilter);
      get().fetchOpenDays();
    } catch (err) {
      set({ error: toMessage(err), loading: false });
      throw err;
    }
  },

  confirmDay: async (date: string) => {
    set({ loading: true, error: null, successMessage: null });

    const payload: FacilityFlowApiControllersPlanningMeTimeClockControllerConfirmDayRequestDto = {
      date,
    };

    try {
      const result = await api.postApiV1MeTimeclockConfirm(payload);
      set({
        ...(date === todayDateString() ? { todayEntry: result } : {}),
        loading: false,
        successMessage: i18n.t('timeclock:messages.dayConfirmed'),
      });

      get().fetchHistory(get().currentFilter);
      get().fetchOpenDays();
    } catch (err) {
      set({ error: toMessage(err), loading: false });
      throw err;
    }
  },

  fetchHistory: async (filter: TimeClockFilterKey) => {
    set({ loading: true, error: null, currentFilter: filter });

    const params: { filter?: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockFilter } = {
      filter: filterMap[filter],
    };

    try {
      const result = await api.getApiV1MeTimeclockHistory(params);
      set({ historyEntries: result ?? [], loading: false });
    } catch (err) {
      set({ error: toMessage(err), loading: false, historyEntries: [] });
    }
  },

  fetchOpenDays: async () => {
    try {
      const result = await api.getApiV1MeTimeclockHistory({ filter: filterMap.open });
      set({ openDays: result ?? [] });
    } catch (err) {
      // why: a background hint — a failure must not replace the page's history with an error
      // state, so it reports through a toast instead of the shared `error` field.
      useToastStore.getState().addToast(toMessage(err), 'error');
    }
  },

  selfCorrectDay: async (
    date: string,
    events: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput[],
  ) => {
    set({ loading: true, error: null, successMessage: null });

    try {
      await customInstance<FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto>({
        url: '/api/v1/me/timeclock/correct',
        method: 'POST',
        headers: { 'Content-Type': 'application/json; version=1.0' },
        data: {
          date,
          correctedEvents: events,
        },
      });
      set({ loading: false, successMessage: i18n.t('timeclock:messages.corrected') });

      await get().fetchToday();
      await get().fetchHistory(get().currentFilter);
      await get().fetchOpenDays();
    } catch (err) {
      set({ error: toMessage(err), loading: false });
      throw err;
    }
  },

  correctDay: async (
    employeeId: string,
    date: string,
    events: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput[],
    correctionNote: string,
  ) => {
    set({ loading: true, error: null, successMessage: null });

    try {
      await adminApi.postApiV1TimeclockAdminEmployeesEmployeeIdDaysDateCorrect(
        employeeId,
        date,
        {
          date,
          targetEmployeeId: employeeId,
          correctedEvents: events,
          correctionNote,
        },
      );
      set({ loading: false, successMessage: i18n.t('timeclock:messages.corrected') });

      await get().fetchToday();
      await get().fetchHistory(get().currentFilter);
      await get().fetchOpenDays();
    } catch (err) {
      set({ error: toMessage(err), loading: false });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
  clearSuccess: () => set({ successMessage: null }),
}));
